// src/pages/LoginPage.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const translations = {
  en: {
    loginTitle: 'Login',
    loginSubtitle: 'Access your account to continue.',
    accountActivated: 'Your account has been activated! You can now log in.',
    passwordResetSuccess: 'Your password has been updated. Please log in.',
    loginIdentifierLabel: 'Email or Username',
    passwordLabel: 'Password',
    forgotPassword: 'Forgot your password?',
    loggingIn: 'Logging in...',
    enter: 'Enter',
    noAccount: "Don't have an account?",
    signUpHere: 'Sign up here',
    fillAllFields: 'Please complete both fields.',
  },
  es: {
    loginTitle: 'Iniciar Sesión',
    loginSubtitle: 'Accede a tu cuenta para continuar.',
    accountActivated: '¡Tu cuenta ha sido activada! Ya puedes iniciar sesión.',
    passwordResetSuccess: 'Tu contraseña ha sido actualizada. Por favor, inicia sesión.',
    loginIdentifierLabel: 'Email o Nombre de Usuario',
    passwordLabel: 'Contraseña',
    forgotPassword: '¿Olvidaste tu contraseña?',
    loggingIn: 'Ingresando...',
    enter: 'Entrar',
    noAccount: '¿No tienes una cuenta?',
    signUpHere: 'Regístrate aquí',
    fillAllFields: 'Por favor, completa ambos campos.',
  },
};

const LoginPage = () => {
  const { lang } = useParams();
  const t = translations[lang] || translations.es;
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      setSuccessMessage(t.accountActivated);
      navigate(`/${lang}/login`, { replace: true });
    }
    if (searchParams.get('reset') === 'success') {
      setSuccessMessage(t.passwordResetSuccess);
      navigate(`/${lang}/login`, { replace: true });
    }

    const newLang = searchParams.get('lang');

    if (newLang == 'es' || newLang == 'en') {
      navigate(`/${newLang}/login`, { replace: true });
    }
  }, [searchParams, navigate, lang, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!loginIdentifier || !password) {
      setError(t.fillAllFields); return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Pasamos lang al backend
      await login(loginIdentifier, password, lang);

      const redirectPath = sessionStorage.getItem('post_login_redirect');

      // if (redirectPath) sessionStorage.removeItem('post_login_redirect');

      navigate(redirectPath || '/user/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-md bg-gray-900/50 border border-yellow-500/20 rounded-xl p-8 shadow-2xl shadow-yellow-500/10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-yellow-400">{t.loginTitle}</h1>
          <p className="text-gray-400 mt-2">{t.loginSubtitle}</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {successMessage && <div className="bg-green-500/20 border-green-500 text-green-300 p-3 rounded-lg mb-4">{successMessage}</div>}
          <div className="mb-4">
            <label htmlFor="loginIdentifier" className="block text-gray-300 text-sm font-bold mb-2">{t.loginIdentifierLabel}</label>
            <input id="loginIdentifier" type="text" value={loginIdentifier} onChange={(e) => setLoginIdentifier(e.target.value)} required className="w-full bg-gray-800 border-gray-700 rounded-lg p-2 text-white" />
          </div>
          <div className="mb-2">
            <label htmlFor="password" className="block text-gray-300 text-sm font-bold mb-2">{t.passwordLabel}</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-gray-800 border-gray-700 rounded-lg p-2 text-white" />
          </div>
          <div className="text-right mb-6">
            <Link to={`/${lang}/forgot-password`} className="text-sm text-yellow-400 hover:text-yellow-300">{t.forgotPassword}</Link>
          </div>
          
          {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}
          
          <button type="submit" disabled={isLoading} className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 px-4 rounded-lg disabled:bg-gray-600">
            {isLoading ? t.loggingIn : t.enter}
          </button>
          
          <p className="text-center text-sm text-gray-500 mt-6">
            {t.noAccount}{' '}
            <Link to={`/${lang}/signup`} className="font-bold text-yellow-400 hover:text-yellow-300">{t.signUpHere}</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;